import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingAmanoParkingReservation,
  BookingContingentReservation,
  BookingParkingReservation,
  BookingTariff,
  BookingTariffType,
  PostProductBooking
} from '@products/models/booking.model';
import { ProductType } from '@products/models/products.model';
import { Tariff, TariffClassification, TariffType, TariffUuids } from '@products/models/tariff.model';
import { AmanaoParkingBookingService } from '@products/services/amano-parking-booking.service';
import { BookingHolderService } from '@products/services/booking-holder.service';
import { ContingentBookingService } from '@products/services/contingent-booking.service';
import { ParkingBookingService } from '@products/services/parking-booking.service';
import { ProductBookingService } from '@products/services/product-booking.service';
import { getSharedBookedPersonIdTariffsTotalCount } from '@store/products/booking/product/product-booking.selectors';
import { getBookedTariffTypeAndTariffByTicketPersonId } from '@store/products/booking/product/tariff/tariff-booking.selectors';
import { combineLatest, first, map, Observable, of, pipe, switchMap, zip } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TariffBookingService {
  private readonly store = inject(Store);
  private readonly productBookingService = inject(ProductBookingService);
  private readonly bookingHolderService = inject(BookingHolderService);
  private readonly contingentBookingService = inject(ContingentBookingService);
  private readonly amanoParkingBookingService = inject(AmanaoParkingBookingService);
  private readonly parkingBookingService = inject(ParkingBookingService);

  prepareRemoveTariffBookingPipe(ticketPersonId: number) {
    return pipe(
      first((combinedLatest: [number, string, BookingTariffType]) =>
        combinedLatest.every((combinedItem) => !!combinedItem)
      ),
      map(([eventId, orderUuid, bookingTariffType]) => ({ eventId, orderUuid, bookingTariffType })),
      switchMap(({ eventId, orderUuid, bookingTariffType }) =>
        zip(
          of({ eventId, orderUuid, bookingTariffType }),
          this.store.select(getSharedBookedPersonIdTariffsTotalCount([bookingTariffType]))
        ).pipe(
          map(([combinedLatest, sharedBookedPersonIdTariffsCount]) => ({
            combinedLatest,
            sharedBookedPersonIdTariffsCount
          }))
        )
      ),
      map(({ combinedLatest, sharedBookedPersonIdTariffsCount }) => {
        const { eventId, orderUuid, bookingTariffType } = combinedLatest;

        const bookingTariff = bookingTariffType.tariffs.find((tariff) => tariff.ticketPersonId === ticketPersonId);

        bookingTariff.count = 0;
        bookingTariff.tariffUuids = [];

        return {
          bookingTariffTypes: [bookingTariffType],
          removePostProductBookings: this.productBookingService.mapBookingProductTypeToPostProductBooking(
            [bookingTariffType],
            eventId,
            orderUuid,
            sharedBookedPersonIdTariffsCount
          )
        };
      })
    );
  }

  removeTariffBookings$(ticketPersonId: number): Observable<{
    bookingTariffTypes: BookingTariffType[];
    removePostProductBookings: PostProductBooking[];
  }> {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(getBookedTariffTypeAndTariffByTicketPersonId(ticketPersonId))
    ]).pipe(this.prepareRemoveTariffBookingPipe(ticketPersonId));
  }

  mapTariffToBookingTariff(
    tariff: Tariff,
    previousCount: number,
    updatedCount: number,
    newPackageIndex?: number
  ): BookingTariff {
    const {
      id,
      name,
      price,
      requiresLegitimation,
      allowedWorkshops,
      allowedWorkshopsFull,
      availableTickets,
      ticketLimit,
      ticketTypeName,
      ticketPersonId,
      ticketPersonTypeId,
      ticketTypeId,
      tariffNumber,
      releasedInMinutes,
      voucherCode,
      voucherType,
      activatedTimestamp,
      sponsors,
      info,
      infoExpanded,
      classification,
      isVisible,
      isVoucher,
      isParkingTicket,
      isLicensePlateMandatory,
      hasDaySellLimit,
      workshopsByDay,
      durationInDays,
      personalizationType,
      documentUploadType,
      validFrom,
      validTill,
      shouldCalendarBeDisplayed,
      daysOffset,
      preferredTicket,
      packageSettings,
      packageNumber,
      packageIndex
    } = tariff;

    // Delete this holder assignment when holders will be assigned in holder reducer
    const newlyAddedTariffsCount = updatedCount - previousCount;
    const isTariffClassificationParking = classification === TariffClassification.Parking;

    const tariffUuids: TariffUuids = this.bookingHolderService.setTariffUuids(
      newlyAddedTariffsCount,
      isTariffClassificationParking
    );

    const contingentReservations: BookingContingentReservation[] =
      this.contingentBookingService.setBookingContingentReservations(
        newlyAddedTariffsCount,
        hasDaySellLimit,
        tariffUuids
      );

    const amanoParkingReservations: BookingAmanoParkingReservation[] =
      this.amanoParkingBookingService.setBookingAmanoParkingReservations(
        newlyAddedTariffsCount,
        isTariffClassificationParking
      );

    const parkingReservations: BookingParkingReservation[] = this.parkingBookingService.setBookingParkingReservations(
      newlyAddedTariffsCount,
      isParkingTicket,
      isLicensePlateMandatory,
      tariffUuids
    );

    const bookingTariffPrice = this.setBookingTariffPrice(price, isTariffClassificationParking);

    const bookingTariff: BookingTariff = {
      ticketTypeId,
      ticketPersonId,
      tariffNumber,
      ticketTypeName,
      ticketPersonTypeId,
      packageNumber,
      packageIndex: newPackageIndex || packageIndex,
      ticketName: name,
      availableTickets,
      ticketLimit,
      voucherCode,
      voucherType,
      personalizationType,
      documentUploadType,
      tariffUuids,
      count: updatedCount,
      price: bookingTariffPrice,
      discountedPrice: 0,
      discountPercentage: 0,
      promoCodeDiscounts: null,
      workshops: [],
      contingentReservations,
      amanoParkingReservations,
      parkingReservations
    };

    return bookingTariff;
  }

  mapTariffTypeToBookingTariffType(
    tariffType: TariffType,
    tariff: Tariff,
    previousCount: number,
    updatedCount: number
  ) {
    const bookingTariff = this.mapTariffToBookingTariff(tariff, previousCount, updatedCount);
    const bookingTariffType: BookingTariffType = {
      productType: ProductType.Tariff,
      productTypeName: tariffType.ticketTypeName,
      ticketTypeId: tariffType.ticketTypeId,
      tariffs: [bookingTariff]
    };

    return bookingTariffType;
  }

  setBookingTariffPrice(price: number, isTariffClassificationParking: boolean) {
    return isTariffClassificationParking ? 0 : price;
  }
}
